import React, { useState, useEffect, useContext } from 'react'
import { useIntl } from 'react-intl'
import { App, Modal, Button, Typography, Tooltip } from 'antd'
import { LoadingOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { addMinutes, isAfter } from 'date-fns'
import Api from '@vacationtracker/shared/services/api'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'

import * as logger from '../../../../services/logger'
import { msAuth, slackAuth, googleAuth } from '../authInstances'
import { useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { notificationStore } from '../../../../context/notificationsContext/store'
import IntlMessages from '../../../../util/IntlMessages'

const { Paragraph } = Typography
const supportLink = 'https://vacationtracker.io/knowledge-base/google-workspace-editing-permissions/'

export const SyncUsersControls = (): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const {
    authUser: { email },
  } = useAppSelector(selectAuthUserSlice)
  const { addActionNotifications } = useContext(notificationStore)

  const [showSyncExistingUsersModal, setShowSyncExistingUsersModal] = useState(false)
  const [isSubmittingSyncExistingUsersRequest, setIsSubmittingSyncExistingUsersRequest] = useState(false)
  const [showMsLoginRequiredModal, setShowMsLoginRequiredModal] = useState(false)
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState(false)
  const [showEnableGoogleApiModal, setShowEnableGoogleApiModal] = useState(false)
  const [msTenantId, setMsTenantId] = useState('')
  const [isMsAdminConsentRequired, setMsAdminConsentRequired] = useState(false)
  const [adminConsentUrl, setAdminConsentUrl] = useState<null | string>(null)
  const [showMsLoginAdditionalScopesRequiredModal, setShowMsLoginAdditionalScopesRequiredModal] = useState(false)
  const [shouldDisableSyncUsers, setShouldDisableSyncUsers] = useState(() => {
    const savedTimestamp = localStorage.getItem('VTSyncUsersTimestamp')
    if (savedTimestamp) {
      const savedTimestampNumber = parseInt(savedTimestamp, 10)
      const currentTime = Date.now()
      const timeDifference = currentTime - savedTimestampNumber
      const fiveMinutesInMs = 5 * 60 * 1000

      return timeDifference < fiveMinutesInMs
    }
    return false
  })

  useEffect(() => {
    if (shouldDisableSyncUsers) {
      const checkInterval = setInterval(() => {
        const savedTimestamp = localStorage.getItem('VTSyncUsersTimestamp')
        if (savedTimestamp) {
          const savedTimestampNumber = parseInt(savedTimestamp, 10)
          const currentTime = Date.now()
          const timeDifference = currentTime - savedTimestampNumber
          const fiveMinutesInMs = 5 * 60 * 1000

          if (timeDifference >= fiveMinutesInMs) {
            setShouldDisableSyncUsers(false)
            localStorage.removeItem('VTSyncUsersTimestamp')
            clearInterval(checkInterval)
          }
        } else {
          setShouldDisableSyncUsers(false)
          clearInterval(checkInterval)
        }
      }, 5000)

      return () => clearInterval(checkInterval)
    }
  }, [shouldDisableSyncUsers])

  if (!authCompany?.platform) {
    throw new Error('Company is required!')
  }

  const errorNotificationHandler = (error, platform, onClickHandler) => {
    const isTokenError = error.message === 'no_tokens'
    const errorMessageDescription = isTokenError ? formatMessage({ id: 'error.token.description' }, { platform }) : error.message
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorMessageDescription,
      key: 'error-notification-handler',
      btn: isTokenError && (
        <Button
          onClick={() => {
            onClickHandler()
            notification.destroy('error-notification-handler')
          }}
        >
          {formatMessage({ id: 'error.token.button' }, { platform })}
        </Button>
      ),
      duration: 0,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorNotification = (error: any, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'notifications.handleSubmitError' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.generic' }),
      description,
      duration: 0,
    })
  }

  const syncUsers = async (token: string, refreshToken?: string) => {
    setIsSubmittingSyncExistingUsersRequest(true)
    const body = {
      eventType: 'SYNC_EXISTING_USERS',
      eventGroup: 'BULK_ACTIONS',
      organizationId: authCompany.organizationId,
      token,
      ...(refreshToken && { refreshToken }),
    }

    const response = await Api.post('/core/event', body)
    setShouldDisableSyncUsers(true)
    localStorage.setItem('VTSyncUsersTimestamp', Date.now().toString())
    setIsSubmittingSyncExistingUsersRequest(false)
    setShowSyncExistingUsersModal(false)
    notification.open({
      key: response.correlationId,
      message: formatMessage({ id: 'users.syncExistingUsers.InProgress' }),
      icon: <LoadingOutlined />,
      duration: 0,
    })
    addActionNotifications([response.correlationId])
  }

  const syncFromMsApiByTeamBase = async (tenantId: string, accessToken: string) => {
    setIsSubmittingSyncExistingUsersRequest(true)
    try {
      const scopes = msAuth.getPermissions(accessToken)
      if (!scopes.toLowerCase().includes('group.read.all')) {
        return setShowMsLoginAdditionalScopesRequiredModal(true)
      }
      const refreshToken = sessionStorage.getItem('msWebAppTokensRefreshToken') || (sessionStorage.getItem('msTokensRefreshToken') as string)
      setShowMsLoginAdditionalScopesRequiredModal(false)
      await syncUsers(accessToken, refreshToken)
    } catch (error) {
      setShowMsLoginAdditionalScopesRequiredModal(false)
      setMsAdminConsentRequired(true)
    }
  }

  const syncFromMsApi = () => {
    setIsSubmittingSyncExistingUsersRequest(true)
    try {
      msAuth.getTokens()
      msAuth
        .refreshTokens()
        .then(async (token) => {
          await syncUsers(token.accessToken)
        })
        .catch((error) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          if (JSON.parse(error)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const err = JSON.parse(error)
            if (err.error === 'invalid_grant' || err.error === 'Authorization_RequestDenied') {
              setShowMsLoginRequiredModal(true)
              return
            }
          }
        })
    } catch (error) {
      setShowMsLoginRequiredModal(true)
    }
  }

  const syncFromGoogleApi = async (): Promise<void> => {
    setIsSubmittingSyncExistingUsersRequest(true)
    const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
    if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
      setShowEnableGoogleApiModal(true)
    } else {
      setShowEnableGoogleApiModal(false)
      try {
        await googleAuth.tokenInfo()
        syncUsers(googleAuth.getAccessToken())
      } catch (error) {
        logger.warning(error)
        setShowEnableGoogleApiModal(true)

        errorNotificationHandler(error, 'Google', getGoogleTokens)
      }
    }
  }

  const syncFromSlackApi = () => {
    try {
      setIsSubmittingSyncExistingUsersRequest(true)

      syncUsers(slackAuth.getUserToken())
    } catch (error) {
      logger.warning({
        errorType: 'ERROR FETCH FROM SLACK API',
        error,
      })
      setShowSyncExistingUsersModal(false)
      errorNotificationHandler(error, 'Slack', getSlackTokens)
    }
  }

  const syncMsUsersWithWebAppTokens = async (tenantId: string) => {
    try {
      if (
        (!sessionStorage.getItem('msWebAppTokensExpiration') || isAfter(new Date(), new Date(sessionStorage.getItem('msWebAppTokensExpiration') as string))) &&
        !showMsLoginAdditionalScopesRequiredModal
      ) {
        setShowMsLoginAdditionalScopesRequiredModal(true)
      } else {
        const { accessToken } = await msAuth.getMSWebAppTokens(tenantId, addMinutes(new Date(), 15).toString(), 'offline_access user.read group.read.all')
        syncFromMsApiByTeamBase(tenantId, accessToken)
        setShowMsLoginAdditionalScopesRequiredModal(false)
      }
    } catch (error) {
      logger.warning(error)
      setShowMsLoginAdditionalScopesRequiredModal(false)
      setMsAdminConsentRequired(true)
    }
  }

  const getBasicMsToken = () => {
    msAuth
      .signin(['user.readbasic.all', 'team.readbasic.all'])
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then(([tokens, msUser, tenantId]) => {
        if (tenantId !== authCompany.organizationId) {
          wrongMicrosoftAccount()
          return
        }
        setShowMsLoginRequiredModal(false)
        return syncFromMsApi()
      })
      .catch((error) => {
        logger.warning(error)
        setShowMsLoginRequiredModal(false)
        setShowSyncExistingUsersModal(false)
        setIsSubmittingSyncExistingUsersRequest(false)
        errorNotificationHandler(error, 'Microsoft', getBasicMsToken)
      })
  }

  const wrongMicrosoftAccount = () => {
    msAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.microsoft.wrongAccount' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.microsoft.wrongMicrosoftAccountDescription' }, { email })}</Paragraph>
          <Button onClick={() => getBasicMsToken()}>{formatMessage({ id: 'error.microsoft.logInWithDifferentAccount' })}</Button>
        </div>
      ),
      duration: 0,
    })
  }

  const wrongSlackAccount = () => {
    slackAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.slack.wrongAccount' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.slack.wrongSlackAccountDescription' })}</Paragraph>
          <Button onClick={() => getSlackTokens()}>{formatMessage({ id: 'error.slack.logInWithDifferentAccount' })}</Button>
        </div>
      ),
      duration: 0,
    })
    setShowSlackLoginRequiredModal(false)
  }

  const getSlackTokens = () => {
    slackAuth
      .signin(SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES)
      .then(async (response) => {
        if ((!response.enterprise && response.team.id !== authCompany.organizationId) || (response.enterprise && response.enterprise?.id !== authCompany.organizationId)) {
          logger.error('WRONG SLACK ACCOUNT', JSON.stringify(response))
          wrongSlackAccount()
          return
        }
        slackAuth.setUserToken(response.authed_user.access_token as string)
        try {
          await Api.post('/slack/update-tokens', {
            token: response.authed_user.access_token,
            botToken: response.access_token,
          })
        } catch (error) {
          logger.warning({
            errorType: 'UPDATE TOKEN ERROR',
            error: error.response,
          })
        }
        setShowSlackLoginRequiredModal(false)
        syncFromSlackApi()
      })
      .catch((error) => {
        logger.warning({
          errorType: 'SLACK SIGNIN ERROR',
          error,
        })
        handleErrorNotification({ message: 'To proceed, please login to your Slack account' }, undefined, 'Missing Slack Scopes')
      })
  }

  const getGoogleTokens = () => {
    googleAuth.signin().then(() => {
      syncFromGoogleApi()
    })
  }

  const syncExistingUsers = async () => {
    if (authCompany.platform === 'microsoft') {
      const tenantId = await Api.get<string>('/microsoft/get-tenant')
      setMsTenantId(tenantId)
      const teamId = authCompany.msTeamId && authCompany.msTeamId !== tenantId ? authCompany.msTeamId : null
      if (teamId) {
        setAdminConsentUrl(`https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${process.env.REACT_APP_MICROSOFT_CLIENT_ID}`)
        syncMsUsersWithWebAppTokens(tenantId)
      } else {
        syncFromMsApi()
      }
    }
    if (authCompany.platform === 'slack') {
      syncFromSlackApi()
    }
    if (authCompany.platform === 'google') {
      getGoogleTokens()
    }
  }

  return (
    <>
      <Tooltip placement="top" title={shouldDisableSyncUsers ? <IntlMessages id="users.syncExistingUsersTooltip" /> : null}>
        <Button onClick={() => setShowSyncExistingUsersModal(!showSyncExistingUsersModal)} disabled={shouldDisableSyncUsers}>
          <IntlMessages id="users.syncExistingUsers" />
        </Button>
      </Tooltip>
      {showSyncExistingUsersModal && (
        <Modal
          title={<IntlMessages id="users.syncExistingUsers" />}
          open={showSyncExistingUsersModal}
          closeIcon={<CloseCircleOutlined />}
          footer={null}
          onCancel={() => setShowSyncExistingUsersModal(false)}
        >
          <p>
            <IntlMessages id="users.aboutToSync" />
          </p>
          <p style={{ textAlign: 'right', marginTop: 20, marginBottom: 0 }}>
            <Button type="primary" loading={isSubmittingSyncExistingUsersRequest} onClick={syncExistingUsers} style={{ marginRight: 5 }}>
              <IntlMessages id="app.ok" />
            </Button>
            <Button onClick={() => setShowSyncExistingUsersModal(false)} style={{ marginRight: 5 }}>
              <IntlMessages id="app.cancel" />
            </Button>
          </p>
        </Modal>
      )}
      {showMsLoginRequiredModal && (
        <Modal
          title={formatMessage({
            id: 'error.microsoft.notificationsLoginRequiredTitle',
          })}
          open={showMsLoginRequiredModal}
          onOk={() => getBasicMsToken()}
          onCancel={() => {
            setIsSubmittingSyncExistingUsersRequest(false)
            setShowMsLoginRequiredModal(false)
            setShowSyncExistingUsersModal(false)
          }}
        >
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showSlackLoginRequiredModal && (
        <Modal
          title={formatMessage({
            id: 'error.slack.usersLoginRequiredTitle',
          })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          onCancel={() => {
            setShowSlackLoginRequiredModal(false)
          }}
        >
          <p>
            <IntlMessages id="slack.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="slack.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showEnableGoogleApiModal && (
        <Modal
          title={formatMessage({
            id: 'error.google.directoryApiDisabledTitle',
          })}
          open={showEnableGoogleApiModal}
          footer={null}
          onCancel={() => {
            setShowEnableGoogleApiModal(false)
          }}
        >
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP1" />
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP2" />
          </Paragraph>
          <Paragraph copyable={{ text: supportLink }}>
            <a href={supportLink} target="_blank" rel="noopener noreferrer">
              {supportLink}
            </a>
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP4" />
          </Paragraph>
        </Modal>
      )}
      {showMsLoginAdditionalScopesRequiredModal && (
        <Modal
          title={formatMessage({
            id: 'error.microsoft.notificationsLoginRequiredTitle',
          })}
          open={showMsLoginAdditionalScopesRequiredModal}
          onOk={() => {
            syncMsUsersWithWebAppTokens(msTenantId)
          }}
          onCancel={() => {
            setShowMsLoginAdditionalScopesRequiredModal(false)
          }}
        >
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      <Modal
        title={formatMessage({
          id: 'microsoft.notificationsLoginRequiredTitle',
        })}
        open={Boolean(authCompany?.platform === 'microsoft' && isMsAdminConsentRequired && adminConsentUrl)}
        onCancel={() => {
          setMsAdminConsentRequired(false)
        }}
        footer={null}
      >
        <Paragraph>
          <IntlMessages id="components.importUsersForm.consent.line1" />
        </Paragraph>
        <Paragraph copyable={{ text: adminConsentUrl as string }}>
          <a href={adminConsentUrl as string}>{adminConsentUrl}</a>
        </Paragraph>
      </Modal>
    </>
  )
}
